import './InlineInputWithIntegratedCheckmark.css';

import React, { useEffect, useRef } from 'react';

import '../../AddablePill/AddablePill.css';

export interface InlineInputWithIntegratedCheckmarkProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
  saving?: boolean;
  /** @default 120 */
  maxLength?: number;
  placeholder?: string;
  /** Passed to the text input’s `aria-label`. */
  inputAriaLabel: string;
  /** Passed to the checkmark button’s `aria-label`. */
  commitAriaLabel: string;
  /** When true, uses slightly larger / bolder input text (e.g. titles). @default true */
  emphasize?: boolean;
}

const DEFAULT_MAX = 120;

const InlineInputWithIntegratedCheckmark: React.FC<
  InlineInputWithIntegratedCheckmarkProps
> = ({
  value,
  onChange,
  onSave,
  onCancel,
  saving = false,
  maxLength = DEFAULT_MAX,
  placeholder = '',
  inputAriaLabel,
  commitAriaLabel,
  emphasize = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSave = (): void => {
    void onSave();
  };

  const rootClass =
    `inline-input-with-checkmark${emphasize ? ' inline-input-with-checkmark--emphasized' : ''}`.trim();

  return (
    <span
      className={`addable-pill-chip addable-pill-input-wrap ${rootClass}`.trim()}
    >
      <input
        ref={inputRef}
        className="addable-pill-input"
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={saving}
        autoComplete="off"
        aria-label={inputAriaLabel}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
          }
          if (e.key === 'Escape') {
            onCancel();
          }
        }}
        onBlur={() => {
          if (!value.trim()) {
            onCancel();
          }
        }}
      />
      <button
        type="button"
        className="addable-pill-commit"
        disabled={saving || !value.trim()}
        aria-label={commitAriaLabel}
        onClick={handleSave}
      >
        ✓
      </button>
    </span>
  );
};

export default InlineInputWithIntegratedCheckmark;
